import { useAuth } from "@clerk/clerk-react";
import { Skeleton } from "Components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "Components/ui/table";
import SectionCard from "Pages/Home/HomeComponents/SectionCard/SectionCard";
import DatePicker from "Pages/Profile/ProfileComponents/Wallet/WalletComponents/DatePicker/DatePicker";
import DepositPopup from "Pages/Profile/ProfileComponents/Wallet/WalletComponents/DepositPopup/DepositPopup";
import WalletDropDown from "Pages/Profile/ProfileComponents/Wallet/WalletComponents/WalletDropDown";
import WithdrawPopup from "Pages/Profile/ProfileComponents/Wallet/WalletComponents/WithdrawPopup/WithdrawPopup";
import UseInnerWidth from "hooks/UseInnerWidth";
import {
  ArrowRight,
  ArrowRightFromLine,
  Codepen,
  CreditCard,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import {
  getLiveChallenges,
  getTransactions,
  getWalletInfo,
} from "../../Redux/Profile/ProfileActions";
import { useNavigate } from "react-router-dom";
import { Separator } from "Components/ui/separator";

function Wallet() {
  const {
    transactions,
    getWalletInfoLoading,
    getWalletInfoError,
    getTransactionsLoading,
    getTransactionsError,
    getLiveChallengesLoading,
  } = useSelector((state) => state.ProfileReducer);

  useEffect(() => {
    if (getWalletInfoError || getTransactionsError) {
      toast.error(getWalletInfoError || getTransactionsError, {
        autoClose: 2000,
        className: "bg-red-opacity text-red",
      });
    }
  }, [getTransactionsError, getWalletInfoError]);

  const [selectedType, setSelectedType] = useState();

  const [selectedStatus, setSelectedStatus] = useState();
  const [startSelectedDate, setStartSelectedDate] = useState(null);
  const [endSelectedDate, setEndSelectedDate] = useState(null);

  const [selectedDeposit, setSelectedDeposit] = useState();
  const [openDepositPopup, setOpenDepositPopup] = useState(false);
  const [depositAmount, setDepositAmount] = useState();

  const [openWithdrawPopup, setOpenWithdrawPopup] = useState(false);
  const [selectedWithdrawCurrency, setSelectedWithdrawCurrency] =
    useState("usd");
  const [withdrawAmount, setWithdrawAmount] = useState();
  const [selectedWithdrawMethod, setSelectedWithdrawMethod] = useState();

  const dispatch = useDispatch();

  const { getToken } = useAuth();
  const width = UseInnerWidth();

  useEffect(() => {
    dispatch(getWalletInfo(getToken));
    dispatch(getTransactions(getToken));
    dispatch(getLiveChallenges(getToken));
  }, []);

  const { walletInfo, liveChallenges } = useSelector(
    (state) => state.ProfileReducer
  );

  useEffect(() => {
    if (
      startSelectedDate ||
      endSelectedDate ||
      selectedType ||
      selectedStatus
    ) {
      dispatch(
        getTransactions(getToken, {
          ...(selectedType && {
            type: selectedType == "all" ? null : selectedType,
          }),
          ...(selectedStatus && {
            status: selectedStatus == "all" ? null : selectedStatus,
          }),
          ...(endSelectedDate &&
            startSelectedDate && {
              from: new Date(startSelectedDate).toLocaleDateString("en-US"),
              to: new Date(endSelectedDate).toLocaleDateString("en-US"),
            }),
        })
      );
    }
  }, [startSelectedDate, endSelectedDate, selectedType, selectedStatus]);

  const types = [
    {
      label: "All",
      value: "all",
    },
    {
      label: "Challenge Purchase",
      value: "CHALLENGE_PURCHASE",
    },
    {
      label: "Withdraw",
      value: "WITHDRAW",
    },
    {
      label: "Transfer",
      value: "TRANSFER",
    },
  ];

  const statuses = [
    {
      label: "All",
      value: "all",
    },
    { label: "Pending", value: "PENDING" },
    { label: "Completed", value: "COMPLETED" },
    { label: "Failed", value: "FAILED" },
  ];

  const navigate = useNavigate();

  return (
    <div
      style={{
        maxWidth: "calc(100vw - 3rem)",
      }}
    >
      <div
        className={`flex ${
          width < 768
            ? "flex-col-reverse"
            : "flex-row-reverse gap-6 justify-between items-center"
        }`}
      >
        <div
          className={`flex flex-col gap-4 h-full justify-between ${
            width < 768 ? "w-full mt-8" : "w-1/3"
          }`}
        >
          <SectionCard
            icon={<Codepen color="var(--primary)" />}
            letter={"N"}
            title={"New Challenge"}
            onClick={() => {
              navigate("/landing");
            }}
          />
          <SectionCard
            icon={<ArrowRight color="#7AB5D2" />}
            title={"Transfer To Wallet"}
            letter={"T"}
            onClick={() => {
              if (liveChallenges?.length > 0) {
                setOpenDepositPopup(true);
              } else {
                toast.error("No Live Challenges Found!", {
                  autoClose: 2000,
                  className: "bg-red-opacity text-red",
                });
              }
            }}
          />
          <SectionCard
            icon={<ArrowRightFromLine color="#83CE94" />}
            title={"Withdraw"}
            letter={"W"}
            onClick={() => setOpenWithdrawPopup(true)}
          />

          <DepositPopup
            open={openDepositPopup}
            setOpen={setOpenDepositPopup}
            selected={selectedDeposit}
            setSelected={(selected) => {
              setSelectedDeposit(selected);
            }}
            value={depositAmount}
            setValue={setDepositAmount}
          />

          <WithdrawPopup
            open={openWithdrawPopup}
            setOpen={setOpenWithdrawPopup}
            currencies={selectedWithdrawCurrency}
            setCurrencies={setSelectedWithdrawCurrency}
            selectedMethod={selectedWithdrawMethod}
            setSelectedMethod={setSelectedWithdrawMethod}
            value={withdrawAmount}
            setValue={setWithdrawAmount}
          />
        </div>
        <div
          className={`grid  flex-1 ${
            width < 768 ? "grid-cols-1" : "grid-cols-2"
          } gap-4`}
        >
          <div
            className={`flex-1 bg-background border-2 border-border-darkNew rounded-lg py-3 px-8 ${
              width >= 768 && "col-span-2"
            }`}
          >
            <div className="text-base font-N_Roman opacity-[28%]">
              Total Balance
            </div>
            {getWalletInfoLoading ? (
              <Skeleton className={`h-8 rounded-md mt-4 w-full`} />
            ) : (
              <div className="text-3xl font-N_Bold">
                {walletInfo?.balance?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                }) ||
                  Number(0).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
              </div>
            )}
          </div>
          <div className="flex-1 bg-background border-2 border-border-darkNew rounded-lg py-3 px-8">
            <div className="text-base font-N_Roman opacity-[28%]">
              Total Withdrawn
            </div>
            {getWalletInfoLoading ? (
              <Skeleton className={`h-8 rounded-md mt-4 w-full`} />
            ) : (
              <div className="text-3xl font-N_Bold">
                {walletInfo?.totalWithdrawn?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                }) ||
                  Number(0).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
              </div>
            )}
          </div>
          <div className="flex-1 bg-background border-2 border-border-darkNew rounded-lg py-3 px-8">
            <div className="text-base font-N_Roman opacity-[28%]">
              Pending Withdrawals
            </div>
            {getWalletInfoLoading ? (
              <Skeleton className={`h-8 rounded-md mt-4 w-full`} />
            ) : (
              <div className="text-3xl font-N_Bold">
                {walletInfo?.pendingWithdrawn?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                }) ||
                  Number(0).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8 h-full bg-background rounded-xl">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center">
          <div className="p-4 text-xl font-N_Medium">Transactions</div>
          <div className="flex flex-col sm:flex-row gap-4 p-4 items-center flex-wrap">
            <DatePicker
              endDateSelected={endSelectedDate}
              setEndDateSelected={setEndSelectedDate}
              startDateSelected={startSelectedDate}
              setStartDateSelected={setStartSelectedDate}
              className={`${width < 600 && "w-full"}`}
            />
            <WalletDropDown
              title={
                types.find((item) => item.value === selectedType)?.label ||
                "Transaction Type"
              }
              data={types}
              selected={selectedType}
              setSelected={setSelectedType}
            />
            <WalletDropDown
              title={
                statuses.find((item) => item.value === selectedStatus)?.label ||
                "Transaction Status"
              }
              data={statuses}
              selected={selectedStatus}
              setSelected={setSelectedStatus}
            />
          </div>
        </div>
        <hr className="opacity-[20%] border-[1.5px]" />

        {transactions?.length === 0 && !getTransactionsLoading ? (
          <div className="text-base text-white-opacity h-[20rem] flex flex-col gap-0 items-center justify-center cursor-pointer ">
            <CreditCard color="var(--white-opacity)" size={"4rem"} />
            <div className="font-N_Medium text-2xl ">
              No Transactions Found!
            </div>
            <div className="font-N_Light text-base w-full max-w-[18rem] text-center">
              Come back later to see your transactions.
            </div>
          </div>
        ) : // Wrap the table in a single scrollable container
        width > 600 ? (
          <div className="rounded-2xl px-4 max-h-[30vh] overflow-x-auto relative hideScrollBar">
            <Table className="w-full border-separate border-spacing-y-3 min-w-full border-spacing-x-0">
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getTransactionsLoading
                  ? Array.from({ length: 3 }).map((_, index) => (
                      <TableRow
                        key={index}
                        className="bg-background-secondary rounded-full"
                      >
                        <TableCell className="rounded-l-2xl py-4">
                          <Skeleton className="h-5 w-16" />
                        </TableCell>
                        <TableCell className="break-keep">
                          <Skeleton className="h-5 w-32" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-5 w-24" />
                        </TableCell>
                        <TableCell className="rounded-r-2xl">
                          <Skeleton className="h-5 w-16" />
                        </TableCell>
                      </TableRow>
                    ))
                  : transactions?.map((item) => (
                      <TableRow
                        key={item.id}
                        className="bg-background-secondary rounded-full"
                      >
                        <TableCell className="rounded-l-2xl py-4 font-N_Medium">
                          {item.type}
                        </TableCell>
                        <TableCell className="opacity-[0.44] break-keep">
                          {new Date(item.createdAt).toLocaleString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                            hour12: true,
                          })}
                        </TableCell>
                        <TableCell>
                          <div
                            className={`flex flex-row items-center gap-2 w-fit p-1 px-4 border-2 rounded-full ${
                              item?.status?.toLowerCase() === "completed"
                                ? "text-green border-green"
                                : item?.status?.toLowerCase() === "failed"
                                ? "text-primary border-primary"
                                : "text-conditions-yellow border-conditions-yellow"
                            }`}
                          >
                            {item.status.charAt(0).toUpperCase() +
                              item.status.slice(1).toLowerCase()}
                          </div>
                        </TableCell>
                        <TableCell className="rounded-r-2xl">
                          {Math.abs(item.amount)?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </div>
        ) : !getTransactionsLoading ? (
          <div className="p-4">
            <div className="flex flex-col gap-4 ">
              {transactions?.map((item, index) => {
                return (
                  <div
                    key={index}
                    className="p-6 bg-background-tradeLocker rounded-lg"
                  >
                    <div className="flex flex-row gap-2 items-center justify-between flex-wrap">
                      <div className="text-lg font-N_Bold">{item.type}</div>
                      <div className="text-lg font-N_Light opacity-[0.4] ml-auto">
                        {" "}
                        {new Date(item.createdAt).toLocaleString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "numeric",
                          minute: "numeric",
                          hour12: true,
                        })}
                      </div>
                    </div>
                    <Separator className="bg-white opacity-[0.3] my-4" />
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-row items-center gap-2 justify-between">
                        <div className="text-lg font-N_Light opacity-[0.3]">
                          Status
                        </div>
                        <div
                          className={`font-N_Medium text-lg ${
                            item?.status?.toLowerCase() === "completed"
                              ? "text-green border-green"
                              : item?.status?.toLowerCase() === "failed"
                              ? "text-primary border-primary"
                              : "text-conditions-yellow"
                          }`}
                        >
                          {item?.status.charAt(0).toUpperCase() +
                            item?.status.slice(1).toLowerCase()}
                        </div>
                      </div>
                      <div className="flex flex-row items-center gap-2 justify-between">
                        <div className="text-lg font-N_Light opacity-[0.3]">
                          Amount
                        </div>
                        <div className={`font-N_Medium text-lg `}>
                          {Math.abs(item.amount)?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="p-4">
            <div className="space-y-6">
              {/* First Transaction */}
              <div className="space-y-4 bg-background-tradeLocker p-6 rounded-lg">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-36" />
                </div>
                <Separator className="bg-white opacity-[0.1] my-4" />
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Status
                    </span>
                    <Skeleton className="h-4 w-16" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Amount
                    </span>
                    <Skeleton className="h-4 w-20" />
                  </div>
                </div>
              </div>

              {/* Second Transaction */}
              <div className="space-y-4 bg-background-tradeLocker p-6 rounded-lg">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-36" />
                </div>
                <Separator className="bg-white opacity-[0.1] my-4" />
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Status
                    </span>
                    <Skeleton className="h-4 w-16" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Amount
                    </span>
                    <Skeleton className="h-4 w-20" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Wallet;
